import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  Stack,
  Chip,
  Divider,
  CircularProgress,
  InputAdornment,
  Tooltip,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const LOCAL_API_BASE_URL = "https://server-9p4hx.kinsta.app";
const LOCAL_TASKS_API_BASE = `${LOCAL_API_BASE_URL}/api/tasks`;
const LOCAL_MASTERDATA_API_BASE = `${LOCAL_API_BASE_URL}/api/masterdata`;
const LLP_BROKEN_DESCRIPTION_PREFIX =
  '[vc_toggle title="Doresc produsul prin LAYAWAY" custom_font_container="tag:h5|font_size:19|text_align:left|line_height:3" custom_use_theme_fonts="yes" use_custom_heading="true" css=".vc_custom_1659790924554{margin-top: -30px !important;}"]';
const LLP_BROKEN_DESCRIPTION_START_REGEX =
  /^\[vc_toggle\b(?=[^\]]*\btitle=(["'])Doresc produsul prin LAYAWAY\1)[^\]]*\]/i;
const LLP_BROKEN_DESCRIPTION_START_HTML_REGEX =
  /^<div[^>]*class=(["'])[^"']*\bvc_toggle\b[^"']*\1[\s\S]{0,800}?Doresc produsul prin LAYAWAY/i;
const LLP_LEADING_HTML_WRAPPER_REGEX =
  /^(?:\s|<p[^>]*>\s*|<\/p>\s*|<br\s*\/?>\s*|&nbsp;\s*)+/i;
const VC_ROW_REGEX = /\[vc_row\b[^\]]*\]/i;
const LAYAWAY_TEXT_REGEX = /Doresc produsul prin LAYAWAY/i;

const LAYAWAY_PLANS = {
  1: {
    label: "1 Rată",
    depositPercent: 0.1,
    rateCount: 1,
    sumoPlanIds: ["10956"],
  },
  2: {
    label: "2 Rate",
    depositPercent: 0.1,
    rateCount: 2,
    sumoPlanIds: ["10956", "10957"],
  },
  3: {
    label: "3 Rate",
    depositPercent: 0.1,
    rateCount: 3,
    sumoPlanIds: ["10956", "10957", "10969"],
  },
  "1s": {
    label: "1 Rată Specială (50% avans)",
    depositPercent: 0.5,
    rateCount: 1,
    sumoPlanIds: ["106724"],
  },
};

const layawayOptions = Object.entries(LAYAWAY_PLANS).map(([value, config]) => ({
  value,
  label: config.label,
}));

const roundTwoDecimals = (value) => Math.round(value * 100) / 100;

const formatAmount = (value) =>
  Number(value).toLocaleString("ro-RO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatWholeAmount = (value) => String(Math.round(Number(value) || 0));

const insertLayawayIntoDescription = (description = "", layawayBlock = "") => {
  const source = String(description || "");
  const block = String(layawayBlock || "").trim();
  if (!block) return source;
  if (!source.trim()) return block;

  const normalizeHeadBeforeLlp = (head = "") => {
    const rawHead = String(head || "")
      .replace(/(?:(?:\s*<p>\s*)?\[vc_separator\](?:\s*<\/p>)?\s*)+$/i, "")
      .trimEnd();
    if (!rawHead) return "";
    return `${rawHead}\n\n[vc_separator]\n\n`;
  };

  const normalizeTailAfterLlp = (tail = "") => {
    const rawTail = String(tail || "");
    const cleanedTail = rawTail
      .replace(
        /^(?:\s*<\/p>\s*<p>\s*)?(?:(?:\s*<p>\s*)?\[vc_separator\](?:\s*<\/p>)?\s*)+/i,
        "",
      )
      .trim();

    if (!cleanedTail) return "";
    return `\n\n${cleanedTail}`;
  };

  const livrareReturRegex =
    /(\[vc_toggle\s+title=("|')LIVRARE\s*(?:(?:&|&amp;)|SI)\s*RETUR\2[\s\S]*?\[\/vc_toggle\])/i;
  const livrareReturMatch = source.match(livrareReturRegex);
  if (livrareReturMatch && Number.isInteger(livrareReturMatch.index)) {
    const start = Number(livrareReturMatch.index);
    const matchedBlock = String(livrareReturMatch[0] || "");
    const end = start + matchedBlock.length;
    const before = normalizeHeadBeforeLlp(source.slice(0, end));
    const after = normalizeTailAfterLlp(source.slice(end));
    return `${before}${block}${after}`.trim();
  }

  const livrareHtmlRegex =
    /(<h[1-6][^>]*>\s*LIVRARE\s*&(?:amp;)?\s*RETUR\s*<\/h[1-6]>[\s\S]*?(?:<\/section>|<\/div>|<\/p>))/i;
  const livrareHtmlMatch = source.match(livrareHtmlRegex);
  if (livrareHtmlMatch && Number.isInteger(livrareHtmlMatch.index)) {
    const start = Number(livrareHtmlMatch.index);
    const matchedBlock = String(livrareHtmlMatch[0] || "");
    const end = start + matchedBlock.length;
    const before = normalizeHeadBeforeLlp(source.slice(0, end));
    const after = normalizeTailAfterLlp(source.slice(end));
    return `${before}${block}${after}`.trim();
  }

  const closingMatches = [
    ...source.matchAll(/\[\/vc_column\]\s*\[\/vc_row\]/gi),
  ];
  if (closingMatches.length > 0) {
    const match = closingMatches[closingMatches.length - 1];
    const idx = Number(match.index || 0);
    const before = normalizeHeadBeforeLlp(source.slice(0, idx));
    const after = normalizeTailAfterLlp(source.slice(idx));
    return `${before}${block}${after}`.trim();
  }

  const brandRegex =
    /(<p[^>]*>(?=[\s\S]*Luxura\s+Elite)(?=[\s\S]*afiliat[ăa])[\s\S]*?<\/p>)/i;
  if (brandRegex.test(source)) {
    return source.replace(brandRegex, `${block}\n\n$1`);
  }

  const brandPlainRegex =
    /(Luxura\s+Elite[\s\S]{0,200}?afiliat[ăa][\s\S]{0,200})/i;
  if (brandPlainRegex.test(source)) {
    return source.replace(brandPlainRegex, `${block}\n\n$1`);
  }

  return `${normalizeHeadBeforeLlp(source)}${block}`.trim();
};

const buildSumoMetaData = (enabled, planIds = []) => {
  const yes = enabled ? "yes" : "";
  const valueOrEmpty = (val) => (enabled ? val : "");
  const planValue = enabled ? { col_1: planIds } : "";

  return [
    { key: "_sumo_pp_enable_sumopaymentplans", value: yes },
    { key: "_sumo_pp_payment_type", value: valueOrEmpty("payment-plans") },
    { key: "_sumo_pp_available_dates_from", value: "" },
    { key: "_sumo_pp_available_dates_to", value: "" },
    { key: "_sumo_pp_apply_global_settings", value: "" },
    { key: "_sumo_pp_force_deposit", value: "" },
    {
      key: "_sumo_pp_default_selected_type",
      value: valueOrEmpty("payment-plans"),
    },
    { key: "_sumo_pp_deposit_type", value: valueOrEmpty("pre-defined") },
    {
      key: "_sumo_pp_deposit_price_type",
      value: valueOrEmpty("fixed-price"),
    },
    { key: "_sumo_pp_pay_balance_type", value: valueOrEmpty("after") },
    { key: "_sumo_pp_pay_balance_after", value: "" },
    { key: "_sumo_pp_pay_balance_before", value: "" },
    { key: "_sumo_pp_pay_balance_before_booked_date", value: "" },
    { key: "_sumo_pp_set_expired_deposit_payment_as", value: "normal" },
    { key: "_sumo_pp_fixed_deposit_price", value: "" },
    { key: "_sumo_pp_fixed_deposit_percent", value: "" },
    {
      key: "_sumo_pp_user_defined_deposit_type",
      value: valueOrEmpty("percent-of-product-price"),
    },
    { key: "_sumo_pp_min_user_defined_deposit_price", value: "" },
    { key: "_sumo_pp_max_user_defined_deposit_price", value: "" },
    { key: "_sumo_pp_min_deposit", value: "" },
    { key: "_sumo_pp_max_deposit", value: "" },
    { key: "_sumo_pp_selected_plans", value: planValue },
  ];
};

const resolveLayawayPlan = (planKey, totalPrice) => {
  if (planKey) return planKey;
  const priceNumber = Number(totalPrice);
  if (Number.isFinite(priceNumber) && priceNumber > 0 && priceNumber <= 2000) {
    return "1s";
  }
  return "";
};

const computePlanBreakdown = (planKey, totalPrice) => {
  const plan = LAYAWAY_PLANS[planKey];
  const priceNumber = Number(totalPrice);
  if (!plan || !Number.isFinite(priceNumber) || priceNumber <= 0) return null;

  const depositAmount = roundTwoDecimals(priceNumber * plan.depositPercent);
  const remainder = roundTwoDecimals(priceNumber - depositAmount);
  const rates = [];

  if (plan.rateCount > 0) {
    const rawRate = remainder / plan.rateCount;
    let collected = 0;
    for (let i = 0; i < plan.rateCount; i += 1) {
      let amount = roundTwoDecimals(rawRate);
      if (i === plan.rateCount - 1) {
        amount = roundTwoDecimals(remainder - collected);
      } else {
        collected = roundTwoDecimals(collected + amount);
      }
      rates.push(amount);
    }
  }

  return {
    depositPercent: plan.depositPercent,
    depositAmount,
    rates,
  };
};

const generateLayawayToggleHtml = (planKey, totalPrice) => {
  const plan = LAYAWAY_PLANS[planKey];
  if (!plan) return "";

  const priceNumber = Number(totalPrice);
  if (!Number.isFinite(priceNumber) || priceNumber <= 0) return "";

  if (planKey === "1s") {
    const advanceLabel = `${Math.round(plan.depositPercent * 100)}%`;
    const advance = Math.round(priceNumber * plan.depositPercent);
    const rate1 = Math.round(priceNumber - advance);
    return `
                [vc_toggle title="Doresc produsul prin LAYAWAY" custom_font_container="tag:h5|font_size:19|text_align:left|line_height:3" custom_use_theme_fonts="yes" use_custom_heading="true" css=".vc_custom_1659790924554{margin-top: -30px !important;}"]
                <table style="height: 133px;">
                    <tbody>
                        <tr>
                            <td style="width: 25.0000%;">
                                <div style="text-align: center;"><span style="color: #000000;"><strong><span style="font-family: Symbol; font-size: 14px;">Avans ${advanceLabel}</span></strong></span></div>
                            </td>
                            <td style="width: 24.8855%;">
                                <div style="text-align: center;"><span style="font-family: Symbol; color: #000000;"><span style="font-size: 14px;"><strong>Rata 1 ${advanceLabel}</strong></span></span></div>
                            </td>
                        </tr>
                        <tr>
                            <td style="width: 25.0000%;">
                                <div style="text-align: center;"><span style="font-size: 12px; color: #000000;">${advance} RON</span></div>
                            </td>
                            <td style="width: 24.8855%;">
                                <div style="text-align: center;"><span style="font-size: 12px; color: #000000;">${rate1} RON</span></div>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <p style="text-align: center;">COMISION <strong>0%</strong></p>
                <p style="text-align: center;" align="center">Acum plătești doar avansul iar noi o să te contactăm în maxim 4 ore pentru semnarea contractului. Totul este online și rapid.</p>
                [/vc_toggle]
            `;
  }

  const advanceValue = Math.round(priceNumber * plan.depositPercent);
  const remainder = Math.max(priceNumber - advanceValue, 0);
  const rateColumns = [{ label: "Rata 1", value: remainder }];
  if (plan.rateCount >= 2) {
    rateColumns.push({ label: "Rata 2", value: Math.round(remainder / 2) });
  }
  if (plan.rateCount >= 3) {
    rateColumns.push({ label: "Rata 3", value: Math.round(remainder / 3) });
  }

  const rateHeaderCells = rateColumns
    .map(
      (col) => `
                            <td style="width: 24.8855%;">
                                <div style="text-align: center;"><span style="font-family: Symbol; color: #000000;"><span style="font-size: 14px;"><strong>${col.label}</strong></span></span></div>
                            </td>`,
    )
    .join("");

  const rateValueCells = rateColumns
    .map(
      (col) => `
                            <td style="width: 24.8855%;">
                                <div style="text-align: center;"><span style="font-size: 12px; color: #000000;">${formatWholeAmount(col.value)} RON</span></div>
                                <div style="text-align: center;"><span style="color: #000000;"><sup><span style="font-size: 12px;">RON/lună</span></sup></span></div>
                            </td>`,
    )
    .join("");

  return `
                [vc_toggle title="Doresc produsul prin LAYAWAY" custom_font_container="tag:h5|font_size:19|text_align:left|line_height:3" custom_use_theme_fonts="yes" use_custom_heading="true" css=".vc_custom_1659790924554{margin-top: -30px !important;}"]
                <table style="height: 133px;">
                    <tbody>
                        <tr>
                            <td style="width: 25.0000%;">
                                <div style="text-align: center;"><span style="color: #000000;"><strong><span style="font-family: Symbol; font-size: 14px;">Avans 10%</span></strong></span></div>
                            </td>
                            ${rateHeaderCells}
                        </tr>
                        <tr>
                            <td style="width: 25.0000%;">
                                <div style="text-align: center;"><span style="font-size: 12px; color: #000000;">${formatWholeAmount(advanceValue)} RON</span></div>
                            </td>
                            ${rateValueCells}
                        </tr>
                    </tbody>
                </table>
                <p style="text-align: center;">COMISION <strong>0%</strong></p>
                <p style="text-align: center;" align="center">Acum plătești doar avansul iar noi o să te contactăm în maxim 4 ore pentru semnarea contractului. Totul este online și rapid.</p>
                [/vc_toggle]
            `;
};

const DESCRIERE_TOGGLE_OPEN =
  '[vc_toggle title="DESCRIERE" custom_font_container="tag:h5|font_size:19|text_align:left|line_height:3" custom_use_theme_fonts="yes" use_custom_heading="true" css=".vc_custom_1659790907923{margin-top: -30px !important;}"]';
const DESCRIERE_TOGGLE_CLOSE = "[/vc_toggle]";
const DESCRIERE_TOGGLE_REGEX =
  /(\[vc_toggle\s+title=("|')DESCRIERE\2[\s\S]*?\])([\s\S]*?)(\s*\[\/vc_toggle\])/i;

const extractDescriereContent = (html) => {
  if (!html) return "";
  const match = html.match(DESCRIERE_TOGGLE_REGEX);
  if (!match) return "";
  return match[3] || "";
};

const replaceDescriereContent = (html, newContent) => {
  const source = html || "";
  const content = newContent ?? "";
  if (DESCRIERE_TOGGLE_REGEX.test(source)) {
    return source.replace(
      DESCRIERE_TOGGLE_REGEX,
      (_match, openTag, _quote, innerContent, closeTag) => {
        const hasLeadingNewline =
          typeof innerContent === "string" && innerContent.startsWith("\n");
        const hasTrailingNewline =
          typeof innerContent === "string" && innerContent.endsWith("\n");
        const prefix = hasLeadingNewline ? "\n" : "";
        const suffix = hasTrailingNewline ? "\n" : "";
        return `${openTag}${prefix}${content}${suffix}${closeTag}`;
      },
    );
  }
  if (!source.trim()) {
    return `${DESCRIERE_TOGGLE_OPEN}\n${content}\n${DESCRIERE_TOGGLE_CLOSE}`;
  }
  return `${source}\n\n${DESCRIERE_TOGGLE_OPEN}\n${content}\n${DESCRIERE_TOGGLE_CLOSE}`;
};

const BLACK_FRIDAY_CATEGORY = {
  id: 2603,
  name: "BlackFriday Exclusive",
  parent: 4526,
};

const parseSelectedPlans = (rawValue) => {
  if (!rawValue) return { planIds: [], planKey: null };
  let parsed = rawValue;
  if (typeof rawValue === "string") {
    try {
      parsed = JSON.parse(rawValue);
    } catch (_) {
      parsed = null;
    }
  }
  const planIds = Array.isArray(parsed?.col_1)
    ? parsed.col_1.map((id) => String(id))
    : [];
  const normalized = [...planIds].sort();
  const match = Object.entries(LAYAWAY_PLANS).find(([, cfg]) => {
    const expected = [...cfg.sumoPlanIds].sort();
    if (expected.length !== normalized.length) return false;
    return expected.every((id, index) => id === normalized[index]);
  });
  return {
    planIds,
    planKey: match ? match[0] : null,
  };
};

const removeLayawayToggle = (html) => {
  try {
    if (!html || typeof html !== "string") return html || "";
    const pattern =
      /(?:(?:\s*<p>\s*)?\[vc_separator\](?:\s*<\/p>)?\s*)*\[vc_toggle\s+title=("|')Doresc produsul prin LAYAWAY\1[\s\S]*?\[\/vc_toggle\](?:(?:\s*<p>\s*)?\[vc_separator\](?:\s*<\/p>)?\s*)*/gi;
    return html.replace(pattern, "").trim();
  } catch (_) {
    return html || "";
  }
};

const normalizeLayawaySeparatorSpacing = (description = "") => {
  const source = String(description || "");
  const layawayWithSeparatorsRegex =
    /(?:(?:\s*<\/p>\s*<p>\s*)?(?:\s*<p>\s*)?\[vc_separator\](?:\s*<\/p>)?\s*)*(\[(?:vc_toggle)\s+title=("|')Doresc produsul prin LAYAWAY\2[\s\S]*?\[\/vc_toggle\])(?:(?:\s*<\/p>\s*<p>\s*)?(?:\s*<p>\s*)?\[vc_separator\](?:\s*<\/p>)?\s*)*/i;

  if (!layawayWithSeparatorsRegex.test(source)) {
    return source;
  }

  return source
    .replace(layawayWithSeparatorsRegex, (_match, layawayBlock) => {
      return `\n\n[vc_separator]\n\n${String(layawayBlock || "").trim()}\n\n`;
    })
    .replace(
      /(\[vc_toggle\s+title=("|')LIVRARE\s*&\s*RETUR\2[\s\S]*?\[\/vc_toggle\])(?:\s*(?:\s*<p>\s*)?\[vc_separator\](?:\s*<\/p>)?\s*)+/i,
      `$1\n\n[vc_separator]\n\n`,
    )
    .trim();
};

const normalizeDescriptionStart = (value = "") => {
  let normalized = String(value || "").replace(/^\uFEFF/, "").trimStart();
  let previous = null;

  while (normalized && normalized !== previous) {
    previous = normalized;
    normalized = normalized
      .replace(LLP_LEADING_HTML_WRAPPER_REGEX, "")
      .trimStart();
  }

  return normalized;
};

const hasBrokenLlpDescriptionPrefix = (description = "") => {
  const normalized = normalizeDescriptionStart(description);
  if (!normalized) return false;

  return (
    normalized.startsWith(LLP_BROKEN_DESCRIPTION_PREFIX) ||
    LLP_BROKEN_DESCRIPTION_START_REGEX.test(normalized) ||
    LLP_BROKEN_DESCRIPTION_START_HTML_REGEX.test(normalized)
  );
};

const stripBrokenLlpPrefixBeforeVcRow = (description = "") => {
  const source = String(description || "");
  const firstVcRowMatch = source.match(VC_ROW_REGEX);
  const firstVcRowIndex =
    firstVcRowMatch && Number.isInteger(firstVcRowMatch.index)
      ? Number(firstVcRowMatch.index)
      : -1;

  if (firstVcRowIndex === -1) {
    return source;
  }

  return source.slice(firstVcRowIndex);
};

const removeBrokenLayawayPrefixBeforeVcRow = (description = "") => {
  const source = String(description || "");
  const firstVcRowMatch = source.match(VC_ROW_REGEX);
  const firstVcRowIndex =
    firstVcRowMatch && Number.isInteger(firstVcRowMatch.index)
      ? Number(firstVcRowMatch.index)
      : -1;

  if (firstVcRowIndex === -1) {
    return source;
  }

  const leadingSegment = source.slice(0, firstVcRowIndex);
  if (
    hasBrokenLlpDescriptionPrefix(source) ||
    LAYAWAY_TEXT_REGEX.test(leadingSegment)
  ) {
    return stripBrokenLlpPrefixBeforeVcRow(source);
  }

  return source;
};

const normalizeText = (text) =>
  text
    ? String(text)
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
    : "";

const stripHtmlTags = (value) => {
  if (!value) return "";
  return String(value)
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const MARKETING_TITLE_PHRASES = [
  "NEW COLLECTION",
  "NEW",
  "HARD TO FIND",
  "HARD-TO-FIND",
  "BEST DEALS",
  "BEST DEAL",
];

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const removeMarketingPhrases = (text) => {
  if (!text) return "";
  let result = text;
  MARKETING_TITLE_PHRASES.forEach((phrase) => {
    const regex = new RegExp(`\\b${escapeRegExp(phrase)}\\b`, "gi");
    result = result.replace(regex, " ");
  });
  return result;
};

const sanitizeProductTitleForGenerator = (title) => {
  const stripped = stripHtmlTags(title || "");
  const withoutMarketing = removeMarketingPhrases(stripped);
  return withoutMarketing.replace(/\s+/g, " ").trim();
};

const isMarketingWord = (value) => {
  if (!value) return false;
  const upper = value.toUpperCase().trim();
  return MARKETING_TITLE_PHRASES.includes(upper);
};

const PRIMARY_CATEGORY_RULES = [
  {
    label: "Genti",
    keywords: [
      "genti",
      "geanta",
      "poseta",
      "posete",
      "bag",
      "clutch",
      "tote",
      "bucket",
    ],
  },
  {
    label: "Incaltaminte",
    keywords: [
      "incaltaminte",
      "pantof",
      "pantofi",
      "sneaker",
      "sneakers",
      "loafer",
      "sandale",
      "boots",
      "ciocate",
      "espadrile",
    ],
  },
  {
    label: "Bijuterii",
    keywords: [
      "bijuterii",
      "inel",
      "ring",
      "bratara",
      "bracelet",
      "colier",
      "necklace",
      "cercei",
      "earring",
      "brooch",
    ],
  },
  {
    label: "Accesorii",
    keywords: [
      "accesorii",
      "curea",
      "belt",
      "portofel",
      "wallet",
      "cardholder",
      "card holder",
      "esarfa",
      "scarf",
      "ochelari",
      "glasses",
      "portcard",
      "manusi",
      "gloves",
      "accessory",
    ],
  },
  {
    label: "Ceasuri",
    keywords: [
      "ceas",
      "watch",
      "chronograph",
      "chronometer",
      "horlog",
      "horologerie",
    ],
  },
];

const detectPrimaryCategory = (text) => {
  if (!text) return "";
  const normalized = normalizeText(text);
  if (!normalized) return "";
  for (const rule of PRIMARY_CATEGORY_RULES) {
    if (rule.keywords.some((keyword) => normalized.includes(keyword))) {
      return rule.label;
    }
  }
  return "";
};

const determinePrimaryCategory = (
  attributeCategory,
  categoriesList = [],
  productName = "",
) => {
  const sources = [];
  if (attributeCategory) sources.push(attributeCategory);
  if (Array.isArray(categoriesList)) {
    categoriesList.forEach((cat) => {
      if (cat?.name) sources.push(cat.name);
    });
  }
  if (productName) sources.push(productName);
  for (const source of sources) {
    const detected = detectPrimaryCategory(source);
    if (detected) return detected;
  }
  return "";
};

const getAttributeValueByKeys = (attributes, keys = []) => {
  if (!Array.isArray(attributes) || !attributes.length) return "";
  const normalizedKeys = keys.map((key) => normalizeText(key));
  const match = attributes.find((attr) => {
    const name = normalizeText(attr?.name);
    const slug = normalizeText(attr?.slug);
    return normalizedKeys.some(
      (target) =>
        (name && name.includes(target)) || (slug && slug.includes(target)),
    );
  });
  if (!match) return "";
  const value = Array.isArray(match.options)
    ? match.options.filter(Boolean).join(", ")
    : match?.option;
  return value ? String(value).trim() : "";
};

const buildDescriptionGeneratorPayload = (product) => {
  if (!product) return null;
  const attributes = Array.isArray(product.attributes)
    ? product.attributes
    : [];
  const pickAttr = (keys) => getAttributeValueByKeys(attributes, keys) || "";

  const brand = sanitizeProductTitleForGenerator(
    pickAttr(["brand", "marca", "marcă"]),
  );
  const model = sanitizeProductTitleForGenerator(pickAttr(["model"]));
  const size = stripHtmlTags(
    pickAttr(["marime", "mărime", "dimensiune", "size"]),
  );
  const color = stripHtmlTags(pickAttr(["culoare", "color"]));
  const material = stripHtmlTags(pickAttr(["material"]));
  const hardware = stripHtmlTags(pickAttr(["hardware", "feronerie"]));
  const year = stripHtmlTags(pickAttr(["an", "an productie", "anul"]));
  const condition = stripHtmlTags(pickAttr(["conditie", "condiție"]));
  const accessories = stripHtmlTags(
    pickAttr(["accesorii", "set complet", "accesorii incluse"]),
  );
  const categoryFromAttr = stripHtmlTags(pickAttr(["categorie", "category"]));

  const categories = Array.isArray(product.categories)
    ? product.categories
    : [];
  const fallbackCategory = determinePrimaryCategory(
    categoryFromAttr,
    categories,
    product?.name || "",
  );
  const safeCategory = fallbackCategory || "Accesorii";

  const formatDimensionValue = (value) => {
    if (!value && value !== 0) return "";
    const raw = stripHtmlTags(String(value));
    if (!raw) return "";
    return /cm$/i.test(raw) ? raw : `${raw} cm`;
  };

  const extraDetailsParts = [];
  const dimensionParts = [];
  if (product.dimensions?.length) {
    const formatted = formatDimensionValue(product.dimensions.length);
    if (formatted) dimensionParts.push(`Lungime: ${formatted}`);
  }
  if (product.dimensions?.height) {
    const formatted = formatDimensionValue(product.dimensions.height);
    if (formatted) dimensionParts.push(`Înălțime: ${formatted}`);
  }
  if (product.dimensions?.width) {
    const formatted = formatDimensionValue(product.dimensions.width);
    if (formatted) dimensionParts.push(`Adâncime: ${formatted}`);
  }
  if (dimensionParts.length) {
    extraDetailsParts.push(`Dimensiuni: ${dimensionParts.join(" | ")}`);
  }
  const shortDescription = stripHtmlTags(product.short_description || "");
  if (shortDescription) {
    extraDetailsParts.push(`Descriere scurtă existentă: ${shortDescription}`);
  }
  const longDescription = stripHtmlTags(product.description || "");
  if (longDescription) {
    extraDetailsParts.push(
      `Descriere actuală: ${longDescription.slice(0, 600)}`,
    );
  }

  const cleanedProductName = sanitizeProductTitleForGenerator(
    product?.name || "",
  );
  const nameWords = cleanedProductName
    .split(/\s+/)
    .map((word) => word.trim())
    .filter(Boolean)
    .filter((word) => !isMarketingWord(word));
  const derivedBrandFromTitle = nameWords[0] || "";
  const derivedModelFromTitle = nameWords.slice(1).join(" ") || "";

  const selectedBrand = isMarketingWord(brand) ? "" : brand;
  let safeBrand = selectedBrand || derivedBrandFromTitle || "";
  if (isMarketingWord(safeBrand)) {
    safeBrand = derivedBrandFromTitle || "";
  }

  let safeModel = model || derivedModelFromTitle || cleanedProductName;
  if (
    safeBrand &&
    safeModel.toLowerCase().startsWith(safeBrand.toLowerCase())
  ) {
    safeModel = safeModel.slice(safeBrand.length).trim();
  }
  safeModel = safeModel || cleanedProductName;

  return {
    brand: safeBrand,
    model: safeModel,
    category: safeCategory,
    size,
    color,
    material,
    hardware,
    year,
    condition,
    accessories,
    language: "ro",
  };
};

const conditionTextToValue = (text) => {
  const normalized = normalizeText(text);
  if (!normalized) return null;
  if (normalized.includes("nou")) return 1;
  if (normalized.includes("excelent")) return 2;
  if (normalized.includes("foarte bun")) return 3;
  if (normalized.includes("buna") || normalized.includes("bun")) return 4;
  if (normalized.includes("rezonabil")) return 5;
  return null;
};

const getConditionValueFromAttributes = (attributes) => {
  try {
    const attrs = Array.isArray(attributes) ? attributes : [];
    const conditionAttr = attrs.find((attr) => {
      const name = normalizeText(attr?.name);
      const slug = normalizeText(attr?.slug);
      return name.includes("conditie") || slug.includes("pa_conditie");
    });
    const option = Array.isArray(conditionAttr?.options)
      ? conditionAttr.options[0]
      : null;
    return conditionTextToValue(option);
  } catch (_) {
    return null;
  }
};

const extractConditionValueFromHtml = (html) => {
  if (!html) return null;
  try {
    const shortcodeMatch = html.match(
      /\[range_shortcode\s+val\s*=\s*["']?(\d+)["']?\s*\]/i,
    );
    if (shortcodeMatch) {
      const parsed = Number(shortcodeMatch[1]);
      return Number.isFinite(parsed) ? parsed : null;
    }
    const blockMatch = html.match(
      /<div[^>]*class=["'][^"']*\brange\b[^"']*["'][\s\S]*?<span[^>]*class=["'][^"']*\blabel\s+(one|two|three|four|five)\s+active[^"']*["']/i,
    );
    if (blockMatch && blockMatch[1]) {
      const pos = blockMatch[1].toLowerCase();
      const map = { one: 1, two: 2, three: 3, four: 3, five: 4 };
      return map[pos] || null;
    }
  } catch (_) {
    return null;
  }
  return null;
};

const normalizeTopConditionBlock = (html, condVal) => {
  if (!html || !condVal) return html || "";
  try {
    const shortcodeRegex =
      /\[range_shortcode\s+val\s*=\s*["']?(\d+)["']?\s*\]/i;
    if (shortcodeRegex.test(html)) {
      return html.replace(shortcodeRegex, `[range_shortcode val="${condVal}"]`);
    }
    const firstBlock = /(\[vc_column_text\])[\s\S]*?(\[\/vc_column_text\])/i;
    if (firstBlock.test(html)) {
      return html.replace(
        firstBlock,
        `$1\n\n[range_shortcode val="${condVal}"]\n\n$2`,
      );
    }
    return (
      `[vc_row][vc_column]\n\n[vc_column_text]\n\n[range_shortcode val="${condVal}"]\n\n[/vc_column_text]\n\n` +
      html
    );
  } catch (_) {
    return html;
  }
};

const transformDescriptionWithCondition = (html, attributes, forcedCondVal) => {
  const cleaned = removeLayawayToggle(html || "");
  let condVal =
    typeof forcedCondVal === "number" && forcedCondVal > 0
      ? forcedCondVal
      : getConditionValueFromAttributes(attributes);
  if (!condVal) {
    condVal = extractConditionValueFromHtml(html || "");
  }
  if (!condVal) condVal = 1;
  return normalizeTopConditionBlock(cleaned, condVal);
};

const extractIDFromSKU = (sku) => {
  const match = sku.match(/0*(\d+)$/);
  return match ? match[1] : null;
};

const buildWooLookupCandidates = (sku, journalNumber) => {
  const rawSku = String(sku || "").trim();
  const compactSku = rawSku.replace(/\s+/g, "");
  const digitsFromSku = extractIDFromSKU(compactSku);
  const journalDigits = String(journalNumber || "")
    .replace(/\D/g, "")
    .replace(/^0+/, "");

  return Array.from(
    new Set(
      [
        rawSku,
        compactSku,
        compactSku.toUpperCase(),
        compactSku.toLowerCase(),
        digitsFromSku,
        journalDigits,
      ].filter(Boolean),
    ),
  );
};

const fetchWooProductFromLocalApi = async ({
  sku,
  journalNumber,
  fallbackId,
}) => {
  const skuCandidates = buildWooLookupCandidates(sku, journalNumber);

  for (const candidate of skuCandidates) {
    try {
      const response = await axios.get(`${LOCAL_TASKS_API_BASE}/woo/product`, {
        params: { sku: candidate },
      });
      if (response.data?.id) {
        return response.data;
      }
    } catch (error) {
      console.error("[WooDetails] SKU lookup failed:", candidate, error);
    }
  }

  if (fallbackId) {
    try {
      const response = await axios.get(`${LOCAL_TASKS_API_BASE}/woo/product`, {
        params: { id: fallbackId },
      });
      if (response.data?.id) {
        return response.data;
      }
    } catch (error) {
      console.error(
        "[WooDetails] ID fallback lookup failed:",
        fallbackId,
        error,
      );
    }
  }

  return null;
};

const WooCommerceProductDetails = ({ sku, journalNumber }) => {
  const [productDetails, setProductDetails] = useState(null);
  const [price, setPrice] = useState("");
  const [salePrice, setSalePrice] = useState("");
  const [dateOnSaleFrom, setDateOnSaleFrom] = useState(null);
  const [dateOnSaleTo, setDateOnSaleTo] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [showBlueAlert, setShowBlueAlert] = useState(false);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [saveError, setSaveError] = useState(null);
  // LLP state
  const [isLayaway, setIsLayaway] = useState(false);
  const [layawayOption, setLayawayOption] = useState("");
  // Existing SUMO payment plans state
  const [existingPlans, setExistingPlans] = useState({
    planIds: [],
    planKey: null,
  });
  const [hasBlackFridayExclusive, setHasBlackFridayExclusive] = useState(false);
  const [productDescriptionContent, setProductDescriptionContent] =
    useState("");
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [descriptionSaving, setDescriptionSaving] = useState(false);
  const [descriptionMessage, setDescriptionMessage] = useState(null);
  const [descriptionError, setDescriptionError] = useState(null);
  const descriptionInputRef = useRef(null);

  const fallbackProductId = useMemo(() => extractIDFromSKU(sku), [sku]);
  const wooProductId = productDetails?.id || fallbackProductId;
  const selectedNumericPrice = useMemo(() => {
    const parsedSale = Number.parseFloat(salePrice);
    if (Number.isFinite(parsedSale) && parsedSale > 0) return parsedSale;
    const parsedRegular = Number.parseFloat(price);
    if (Number.isFinite(parsedRegular) && parsedRegular > 0)
      return parsedRegular;
    return null;
  }, [price, salePrice]);

  const conditionValue = useMemo(() => {
    if (!productDetails) return null;
    const attrValue = getConditionValueFromAttributes(
      productDetails.attributes,
    );
    if (attrValue) return attrValue;
    const htmlValue = extractConditionValueFromHtml(
      productDetails.description || "",
    );
    return htmlValue || 1;
  }, [productDetails]);

  const applyProductDataToState = (data) => {
    if (!data) {
      setProductDetails(null);
      setPrice("");
      setSalePrice("");
      setDateOnSaleFrom(null);
      setDateOnSaleTo(null);
      setExistingPlans({ planIds: [], planKey: null });
      setIsLayaway(false);
      setLayawayOption("");
      setHasBlackFridayExclusive(false);
      setProductDescriptionContent("");
      return;
    }

    setProductDetails(data);
    setPrice(data.regular_price || "");
    setSalePrice(data.sale_price || "");
    setDateOnSaleFrom(
      data.date_on_sale_from ? dayjs(data.date_on_sale_from).utc() : null,
    );
    setDateOnSaleTo(
      data.date_on_sale_to ? dayjs(data.date_on_sale_to).utc() : null,
    );

    let enableLLP = false;
    let parsedPlans = { planIds: [], planKey: null };
    if (Array.isArray(data.meta_data)) {
      const enableMeta = data.meta_data.find(
        (m) => m.key === "_sumo_pp_enable_sumopaymentplans",
      );
      const selectedMeta = data.meta_data.find(
        (m) => m.key === "_sumo_pp_selected_plans",
      );
      enableLLP = enableMeta ? enableMeta.value === "yes" : false;
      parsedPlans = parseSelectedPlans(selectedMeta?.value || null);
    }
    setIsLayaway(enableLLP);
    setLayawayOption(
      enableLLP && parsedPlans.planKey ? parsedPlans.planKey : "",
    );
    setExistingPlans(parsedPlans);

    setHasBlackFridayExclusive(
      (Array.isArray(data.categories) ? data.categories : []).some(
        (category) => Number(category?.id) === BLACK_FRIDAY_CATEGORY.id,
      ),
    );

    setProductDescriptionContent(
      extractDescriereContent(data.description || ""),
    );
  };

  useEffect(() => {
    const fetchProductDetails = async () => {
      if (!sku && !fallbackProductId) {
        applyProductDataToState(null);
        return;
      }

      setLoading(true);
      try {
        const data = await fetchWooProductFromLocalApi({
          sku,
          journalNumber,
          fallbackId: fallbackProductId,
        });
        applyProductDataToState(data);
        setSaveMessage(null);
        setSaveError(null);
        setDescriptionMessage(null);
        setDescriptionError(null);
        setGenerateError(null);
      } catch (error) {
        console.error(
          "Error fetching product details from WooCommerce:",
          error,
        );
        applyProductDataToState(null);
        setSaveError(
          "Nu am putut încărca detaliile produsului din WooCommerce.",
        );
        setDescriptionError(
          "Nu am putut încărca detaliile produsului din WooCommerce.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [fallbackProductId, journalNumber, sku]);

  useEffect(() => {
    if (!isLayaway) return;
    if (
      Number.isFinite(selectedNumericPrice) &&
      selectedNumericPrice !== null &&
      selectedNumericPrice <= 2000 &&
      layawayOption !== "1s"
    ) {
      setLayawayOption("1s");
    }
  }, [isLayaway, selectedNumericPrice, layawayOption]);

  const refreshProductDetailsState = async () => {
    if (!sku && !wooProductId) return;
    try {
      const data = await fetchWooProductFromLocalApi({
        sku,
        journalNumber,
        fallbackId: wooProductId,
      });
      applyProductDataToState(data);
    } catch (error) {
      console.error("Error refreshing product details:", error);
    }
  };
  // Helper: Set SUMO Payment Plans meta_data for product (refactored for real SUMO meta)
  const setSumoPaymentPlans = async (productId, layawayOption, isLayaway) => {
    try {
      let sumoMetaData;

      if (isLayaway) {
        const plan = LAYAWAY_PLANS[layawayOption];
        if (!plan) return;
        sumoMetaData = buildSumoMetaData(true, plan.sumoPlanIds);
      } else {
        sumoMetaData = buildSumoMetaData(false, []);
      }

      await axios.put(`${LOCAL_TASKS_API_BASE}/woo/product/${productId}`, {
        meta_data: sumoMetaData,
      });
    } catch (err) {
      console.error("Error updating SUMO Payment Plans:", err);
    }
  };

  useEffect(() => {
    if (price && salePrice) {
      const priceNum = parseFloat(price);
      const salePriceNum = parseFloat(salePrice);
      const discount = ((priceNum - salePriceNum) / priceNum) * 100;
      setDiscountPercentage(discount.toFixed(2));

      if (salePriceNum < priceNum * 0.75) {
        setShowAlert(true);
        setShowBlueAlert(false);
      } else if (salePriceNum < priceNum) {
        setShowAlert(false);
        setShowBlueAlert(true);
      } else {
        setShowAlert(false);
        setShowBlueAlert(false);
      }
    } else {
      setShowAlert(false);
      setShowBlueAlert(false);
      setDiscountPercentage(0);
    }
  }, [price, salePrice]);

  const logPriceChange = async ({ oldPrice, newPrice, priceType }) => {
    if (
      !productDetails?.sku ||
      newPrice === undefined ||
      newPrice === null ||
      newPrice === ""
    ) {
      return;
    }

    try {
      await axios.post(
        `${LOCAL_MASTERDATA_API_BASE}/product-price-history/log`,
        {
          sku: productDetails.sku,
          oldPrice,
          newPrice,
          source: "ui-woo-product-details",
          meta: {
            priceType,
            productId: wooProductId,
            journalNumber,
          },
        },
      );
    } catch (error) {
      console.error("Error logging price history:", error);
    }
  };

  const handleToggleBlackFridayCategory = (checked) => {
    setHasBlackFridayExclusive(checked);
  };

  const buildFinalDescription = () => {
    if (!productDetails) return "";
    const baseDescription = transformDescriptionWithCondition(
      productDetails.description || "",
      productDetails.attributes || [],
      conditionValue,
    );
    const descriptionWithoutBrokenPrefix = removeBrokenLayawayPrefixBeforeVcRow(
      baseDescription || "",
    );
    const descriptionWithCustomBlock = replaceDescriereContent(
      descriptionWithoutBrokenPrefix || "",
      productDescriptionContent,
    );
    const effectiveLayawayOption = resolveLayawayPlan(
      layawayOption,
      selectedNumericPrice,
    );
    const shouldRenderLayaway =
      isLayaway &&
      effectiveLayawayOption &&
      Number.isFinite(selectedNumericPrice) &&
      selectedNumericPrice;
    const layawayBlock = shouldRenderLayaway
      ? generateLayawayToggleHtml(effectiveLayawayOption, selectedNumericPrice)
      : "";
    return normalizeLayawaySeparatorSpacing(
      insertLayawayIntoDescription(descriptionWithCustomBlock, layawayBlock),
    );
  };

  const handleApplyFormatting = (format) => {
    const textarea = descriptionInputRef.current;
    if (!textarea) return;
    const { selectionStart, selectionEnd, value } = textarea;
    if (
      selectionStart === null ||
      selectionEnd === null ||
      selectionStart === selectionEnd
    ) {
      return;
    }
    const selectedText = value.slice(selectionStart, selectionEnd);
    if (!selectedText) return;

    let formattedText = selectedText;
    if (format === "bold") {
      formattedText = `<strong>${selectedText}</strong>`;
    } else if (format === "italic") {
      formattedText = `<em>${selectedText}</em>`;
    } else if (format === "list") {
      const items = selectedText
        .split("\n")
        .map((item) => item.trim())
        .filter(Boolean)
        .map((item) => `<li>${item}</li>`)
        .join("\n");
      if (!items) return;
      formattedText = `<ul>\n${items}\n</ul>`;
    }

    const before = value.slice(0, selectionStart);
    const after = value.slice(selectionEnd);
    const newValue = `${before}${formattedText}${after}`;
    setProductDescriptionContent(newValue);

    requestAnimationFrame(() => {
      try {
        textarea.focus();
        const start = selectionStart;
        const end = selectionStart + formattedText.length;
        textarea.setSelectionRange(start, end);
      } catch (_) {
        // ignore selection errors
      }
    });
  };

  const handleGenerateDescription = async () => {
    if (!productDetails) return;
    setIsGeneratingDescription(true);
    setGenerateError(null);
    setDescriptionMessage(null);
    setDescriptionError(null);
    try {
      const payload = buildDescriptionGeneratorPayload(productDetails);
      if (!payload) {
        setGenerateError("Nu avem date suficiente pentru generare.");
        setIsGeneratingDescription(false);
        return;
      }
      const response = await axios.post(
        `${LOCAL_MASTERDATA_API_BASE}/generate-product-description`,
        payload,
      );
      const generatedHtml = response.data?.fullHtml;
      if (generatedHtml) {
        setProductDescriptionContent(generatedHtml);
      } else {
        setGenerateError("Nu am primit conținut generat.");
      }
    } catch (error) {
      console.error("Error generating product description:", error);
      const apiError =
        error?.response?.data?.error || "Nu am putut genera descrierea.";
      setGenerateError(apiError);
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const handleDescriptionSave = async () => {
    if (!wooProductId || !productDetails) return;

    setDescriptionSaving(true);
    setDescriptionMessage(null);
    setDescriptionError(null);
    setGenerateError(null);

    const finalDescription = buildFinalDescription();

    try {
      await axios.put(`${LOCAL_TASKS_API_BASE}/woo/product/${wooProductId}`, {
        description: finalDescription,
      });

      await refreshProductDetailsState();
      setDescriptionMessage("Descrierea a fost actualizată.");
    } catch (error) {
      console.error("Error saving product description:", error);
      setDescriptionError("Nu am putut salva descrierea.");
    } finally {
      setDescriptionSaving(false);
    }
  };

  const handleSave = async () => {
    if (!wooProductId || !productDetails) {
      return;
    }

    setSaving(true);
    setSaveMessage(null);
    setSaveError(null);

    // Compose payload for product update
    let payload = {
      regular_price: price || "",
      sale_price: salePrice || "",
      date_on_sale_from: dateOnSaleFrom
        ? dateOnSaleFrom.utc().toISOString()
        : null,
      date_on_sale_to: dateOnSaleTo ? dateOnSaleTo.utc().toISOString() : null,
    };
    const originalCategories = Array.isArray(productDetails.categories)
      ? productDetails.categories
      : [];
    const originalHasBlackFridayCategory = originalCategories.some(
      (category) => Number(category?.id) === BLACK_FRIDAY_CATEGORY.id,
    );
    const shouldUpdateCategories =
      originalHasBlackFridayCategory !== hasBlackFridayExclusive;
    if (shouldUpdateCategories) {
      const categoriesMap = new Map();
      originalCategories.forEach((category) => {
        if (!category || category.id === undefined || category.id === null) {
          return;
        }
        const categoryId = Number(category.id);
        if (!Number.isFinite(categoryId)) {
          return;
        }
        categoriesMap.set(categoryId, { id: categoryId });
      });
      if (hasBlackFridayExclusive) {
        categoriesMap.set(BLACK_FRIDAY_CATEGORY.id, {
          id: BLACK_FRIDAY_CATEGORY.id,
        });
      } else {
        categoriesMap.delete(BLACK_FRIDAY_CATEGORY.id);
      }
      payload.categories = Array.from(categoriesMap.values());
    }

    const finalDescription = buildFinalDescription();

    payload.description = finalDescription;

    try {
      await axios.put(
        `${LOCAL_TASKS_API_BASE}/woo/product/${wooProductId}`,
        payload,
      );

      await axios.post(`${LOCAL_MASTERDATA_API_BASE}/update-price`, {
        sku: productDetails.sku,
        newPrice: price,
        oldPrice: productDetails.regular_price,
        productName: productDetails.name,
        journalNumber,
        salePrice,
        skipWooUpdate: true,
      });

      if (price !== productDetails.regular_price) {
        await logPriceChange({
          oldPrice: productDetails.regular_price,
          newPrice: price,
          priceType: "regular",
        });
      }

      if (salePrice) {
        await logPriceChange({
          oldPrice: productDetails.sale_price || null,
          newPrice: salePrice,
          priceType: "sale",
        });
      }

      // Update SUMO Payment Plans (LLP) meta_data
      await setSumoPaymentPlans(
        wooProductId,
        resolveLayawayPlan(layawayOption, selectedNumericPrice),
        isLayaway,
      );
      setSaveMessage("Detaliile produsului au fost actualizate.");
      await refreshProductDetailsState();
    } catch (error) {
      console.error("Error updating product details:", error);
      setSaveError("Actualizarea a eșuat. Verifică log-urile WooCommerce.");
    } finally {
      setSaving(false);
    }
  };

  const renderPriceField = ({ label, value, onChange, helperText }) => (
    <TextField
      label={label}
      value={value}
      onChange={onChange}
      fullWidth
      variant="outlined"
      margin="normal"
      InputProps={{
        startAdornment: <InputAdornment position="start">RON</InputAdornment>,
        sx: { borderRadius: 2 },
      }}
      helperText={helperText}
    />
  );

  const headerBackground = "linear-gradient(120deg, #0ea5e9 0%, #6366f1 100%)";

  return (
    <Paper
      sx={{
        p: 4,
        m: 3,
        borderRadius: 4,
        boxShadow: "0 24px 60px rgba(15, 23, 42, 0.12)",
        background: "#f8faff",
      }}
    >
      <Box
        sx={{
          background: headerBackground,
          borderRadius: 3,
          p: 3,
          color: "white",
          mb: 3,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Actualizare preț Online
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            Modificările sunt trimise direct în WooCommerce și logate în
            masterdata.
          </Typography>
        </Box>
        {productDetails?.sku && (
          <Chip
            label={`SKU ${productDetails.sku}`}
            color="primary"
            sx={{ backgroundColor: "rgba(255,255,255,0.2)", color: "white" }}
          />
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : !productDetails ? (
        <Alert severity="warning">
          Nu am găsit detalii WooCommerce pentru acest SKU.
        </Alert>
      ) : (
        <Stack spacing={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: "1px solid #e2e8f0",
              backgroundColor: "white",
            }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    bgcolor: "#f1f5f9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 2,
                    minHeight: 220,
                  }}
                >
                  {productDetails.images?.[0]?.src ? (
                    <img
                      src={productDetails.images[0].src}
                      alt={productDetails.name}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Fără imagine disponibilă
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={8}>
                <Stack spacing={1.5}>
                  <Typography variant="h6" fontWeight={700}>
                    {productDetails.name}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {productDetails.status && (
                      <Chip
                        label={`Status: ${productDetails.status}`}
                        size="small"
                        color={
                          productDetails.status === "publish"
                            ? "success"
                            : "default"
                        }
                      />
                    )}
                    {productDetails.type && (
                      <Chip
                        label={`Tip: ${productDetails.type}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {productDetails.stock_status && (
                      <Chip
                        label={`Stoc: ${productDetails.stock_status}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {conditionValue && (
                      <Tooltip title="Shortcode condiție (salvat în descriere)">
                        <Chip
                          label={`[range_shortcode val="${conditionValue}"]`}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      </Tooltip>
                    )}
                  </Stack>
                  <Divider sx={{ my: 1 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Preț curent
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {productDetails.price
                          ? `${productDetails.price} RON`
                          : "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Preț întreg
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {productDetails.regular_price
                          ? `${productDetails.regular_price} RON`
                          : "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Preț promo
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {productDetails.sale_price
                          ? `${productDetails.sale_price} RON`
                          : "—"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Început promo
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {productDetails.date_on_sale_from
                          ? dayjs(productDetails.date_on_sale_from).format(
                              "DD.MM.YYYY",
                            )
                          : "—"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          {saveMessage && (
            <Alert severity="success" onClose={() => setSaveMessage(null)}>
              {saveMessage}
            </Alert>
          )}
          {saveError && (
            <Alert severity="error" onClose={() => setSaveError(null)}>
              {saveError}
            </Alert>
          )}

          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: "1px solid #e2e8f0",
              backgroundColor: "white",
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Descriere produs
            </Typography>
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleApplyFormatting("bold")}
                  disabled={!productDetails}
                >
                  Bold
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleApplyFormatting("italic")}
                  disabled={!productDetails}
                >
                  Italic
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleApplyFormatting("list")}
                  disabled={!productDetails}
                >
                  Listă
                </Button>
              </Stack>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems="stretch"
              >
                <TextField
                  multiline
                  minRows={12}
                  value={productDescriptionContent}
                  onChange={(e) => setProductDescriptionContent(e.target.value)}
                  fullWidth
                  inputRef={descriptionInputRef}
                  disabled={!productDetails}
                  placeholder="Introdu sau generează descrierea produsului"
                  sx={{
                    "& .MuiInputBase-input": {
                      fontFamily: "monospace",
                      fontSize: 14,
                    },
                  }}
                />
                <Stack spacing={1} minWidth={{ xs: "100%", md: 180 }}>
                  <Button
                    variant="outlined"
                    onClick={handleGenerateDescription}
                    disabled={isGeneratingDescription || !productDetails}
                  >
                    {isGeneratingDescription ? "Se generează..." : "Generează"}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleDescriptionSave}
                    disabled={descriptionSaving || !productDetails}
                  >
                    {descriptionSaving ? "Se salvează..." : "SAVE"}
                  </Button>
                </Stack>
              </Stack>
              {generateError && (
                <Alert severity="error" onClose={() => setGenerateError(null)}>
                  {generateError}
                </Alert>
              )}
              {descriptionError && (
                <Alert
                  severity="error"
                  onClose={() => setDescriptionError(null)}
                >
                  {descriptionError}
                </Alert>
              )}
              {descriptionMessage && (
                <Alert
                  severity="success"
                  onClose={() => setDescriptionMessage(null)}
                >
                  {descriptionMessage}
                </Alert>
              )}
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: "1px solid #e2e8f0",
              backgroundColor: "white",
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Ajustare prețuri
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                {renderPriceField({
                  label: "Preț regular",
                  value: price,
                  onChange: (e) => setPrice(e.target.value),
                  helperText: "Prețul principal afișat pe site",
                })}
                {showAlert && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Atenție: reducerea depășește 25% din prețul inițial.
                    Verifică politica de discount.
                  </Alert>
                )}
                {showBlueAlert && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Reducerea curentă este {discountPercentage}% din prețul
                    întreg.
                  </Alert>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                {renderPriceField({
                  label: "Preț promoțional",
                  value: salePrice,
                  onChange: (e) => setSalePrice(e.target.value),
                  helperText: "Lasă câmpul gol pentru a elimina promoția",
                })}
                <Grid container spacing={2} sx={{ mt: 0 }}>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Start promoție"
                        value={dateOnSaleFrom}
                        onChange={(newValue) =>
                          setDateOnSaleFrom(
                            newValue ? dayjs(newValue).utc() : null,
                          )
                        }
                        renderInput={(params) => (
                          <TextField {...params} fullWidth margin="normal" />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Sfârșit promoție"
                        value={dateOnSaleTo}
                        onChange={(newValue) =>
                          setDateOnSaleTo(
                            newValue ? dayjs(newValue).utc() : null,
                          )
                        }
                        renderInput={(params) => (
                          <TextField {...params} fullWidth margin="normal" />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* LLP/Rate UI */}
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Rate
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isLayaway}
                  onChange={(e) => setIsLayaway(e.target.checked)}
                />
              }
              label="Rate disponibile?"
            />
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Checkbox
                  checked={hasBlackFridayExclusive}
                  onChange={(e) =>
                    handleToggleBlackFridayCategory(e.target.checked)
                  }
                  disabled={!productDetails}
                />
              }
              label="Special > BlackFriday Exclusive"
            />
            {isLayaway && (
              <>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel>Număr rate</InputLabel>
                  <Select
                    value={layawayOption}
                    onChange={(e) => setLayawayOption(e.target.value)}
                    label="Număr rate"
                  >
                    {layawayOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Existing SUMO payment plans display */}
                {existingPlans?.planIds?.length > 0 && (
                  <Paper
                    elevation={0}
                    sx={{
                      mt: 3,
                      mb: 1,
                      p: 2,
                      borderRadius: 2,
                      background: "#f8fafc",
                      border: "1px solid #dbeafe",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      gutterBottom
                    >
                      Rate existente
                    </Typography>
                    {(() => {
                      const derivePrice = () => {
                        const sale = Number.parseFloat(
                          productDetails?.sale_price,
                        );
                        if (Number.isFinite(sale) && sale > 0) return sale;
                        const current = Number.parseFloat(
                          productDetails?.price,
                        );
                        if (Number.isFinite(current) && current > 0)
                          return current;
                        const regular = Number.parseFloat(
                          productDetails?.regular_price,
                        );
                        if (Number.isFinite(regular) && regular > 0)
                          return regular;
                        return null;
                      };
                      const selectedPrice = derivePrice();
                      const rows = [];

                      if (
                        existingPlans.planKey &&
                        selectedPrice &&
                        LAYAWAY_PLANS[existingPlans.planKey]
                      ) {
                        const plan = LAYAWAY_PLANS[existingPlans.planKey];
                        const breakdown = computePlanBreakdown(
                          existingPlans.planKey,
                          selectedPrice,
                        );
                        if (breakdown) {
                          const percentLabel = Math.round(
                            plan.depositPercent * 100,
                          );
                          rows.push({
                            key: plan.sumoPlanIds.join("-"),
                            label: plan.label,
                            avans: `${percentLabel}% (${formatAmount(
                              breakdown.depositAmount,
                            )} RON)`,
                            rates:
                              breakdown.rates.length > 0
                                ? breakdown.rates
                                    .map(
                                      (value) => `${formatAmount(value)} RON`,
                                    )
                                    .join(", ")
                                : "—",
                          });
                        }
                      }

                      if (!rows.length) {
                        existingPlans.planIds.forEach((planId) => {
                          rows.push({
                            key: planId,
                            label: `Plan ID: ${planId}`,
                            avans: "-",
                            rates: "-",
                          });
                        });
                      }

                      if (!rows.length) {
                        return (
                          <Typography variant="body2" color="text.secondary">
                            Nu există detalii pentru rate.
                          </Typography>
                        );
                      }

                      return (
                        <Box sx={{ mt: 1 }}>
                          <Table size="small" sx={{ background: "#f8fafc" }}>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>
                                  Plan
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>
                                  Avans
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>
                                  Rate
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {rows.map((row) => (
                                <TableRow key={row.key}>
                                  <TableCell>{row.label}</TableCell>
                                  <TableCell>{row.avans}</TableCell>
                                  <TableCell>{row.rates}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      );
                    })()}
                  </Paper>
                )}
              </>
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 3,
              }}
            >
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => {
                  setPrice(productDetails.regular_price || "");
                  setSalePrice(productDetails.sale_price || "");
                  setDateOnSaleFrom(
                    productDetails.date_on_sale_from
                      ? dayjs(productDetails.date_on_sale_from).utc()
                      : null,
                  );
                  setDateOnSaleTo(
                    productDetails.date_on_sale_to
                      ? dayjs(productDetails.date_on_sale_to).utc()
                      : null,
                  );
                  setSaveMessage(null);
                  setSaveError(null);
                  setDescriptionMessage(null);
                  setDescriptionError(null);
                  setGenerateError(null);
                  // LLP: reset layaway UI state
                  let enableLLP = false;
                  let parsedPlans = { planIds: [], planKey: null };
                  if (Array.isArray(productDetails.meta_data)) {
                    const enableMeta = productDetails.meta_data.find(
                      (m) => m.key === "_sumo_pp_enable_sumopaymentplans",
                    );
                    const selectedMeta = productDetails.meta_data.find(
                      (m) => m.key === "_sumo_pp_selected_plans",
                    );
                    enableLLP = enableMeta ? enableMeta.value === "yes" : false;
                    parsedPlans = parseSelectedPlans(
                      selectedMeta?.value || null,
                    );
                  }
                  setIsLayaway(enableLLP);
                  setLayawayOption(
                    enableLLP && parsedPlans.planKey ? parsedPlans.planKey : "",
                  );
                  setExistingPlans(parsedPlans);
                  const categories = Array.isArray(productDetails.categories)
                    ? productDetails.categories
                    : [];
                  setHasBlackFridayExclusive(
                    categories.some(
                      (category) =>
                        Number(category?.id) === BLACK_FRIDAY_CATEGORY.id,
                    ),
                  );
                  setProductDescriptionContent(
                    extractDescriereContent(productDetails.description || ""),
                  );
                }}
              >
                Resetează
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={saving}
                sx={{ px: 4, fontWeight: 700 }}
              >
                {saving ? "Se salvează..." : "Salvează modificările"}
              </Button>
            </Box>
          </Paper>
        </Stack>
      )}
    </Paper>
  );
};

export default WooCommerceProductDetails;
